import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CircleDot,
  Clock,
  Download,
  Eye,
  FileText,
  Image as ImageIcon,
  Lock,
  Paperclip,
} from "lucide-react";
import { useMemo, useState } from "react";
import { ErrorAlert, LoadingSpinner } from "../../../components/ui";
import { useAuthUser } from "../../../stores/authStore";
import { EtiquetaAcoesCliente, EtiquetaStatusEquipe } from "../../equipe/AcoesRapidasCartao";
import type {
  AnexoChamado,
  DetalheChamado,
  InteracaoChamado,
} from "../../../types/portalCliente";
import {
  baixarAnexo,
  useAnexosChamado,
  useCapacidades,
  useDetalheChamado,
  useSlaChamado,
} from "./api";
import type { SlaInfo } from "../../../types/api";
import { formatTempoRestante } from "../../../utils/chamadoUtils";
import { CompositorInteracao } from "./CompositorInteracao";
import { VisualizadorAnexo } from "./VisualizadorAnexo";
import {
  agruparPorDia,
  anexosPorInteracao,
  ehEvento,
  iniciais,
  nomeAutor,
} from "./linhaDoTempo";
import {
  classesSeveridade,
  formatarData,
  formatarTamanho,
  rotuloSeveridade,
} from "./severidade";

const ChamadoDetalhePage = () => {
  const { id } = useParams<{ id: string }>();
  const idChamado = Number(id);
  const equipe = useAuthUser()?.equipe ?? false;

  const { data, isLoading, isError, error, refetch } =
    useDetalheChamado(idChamado);
  const anexos = useAnexosChamado(idChamado);

  // O visualizador vive na página, e não em cada painel: o mesmo anexo é aberto
  // tanto pela lista lateral quanto pela linha do tempo.
  const [visualizando, setVisualizando] = useState<number | null>(null);
  const exibiveis = (anexos.data ?? []).filter((a) => a.exibivelInline);
  const abrirVisualizador = (anexo: AnexoChamado) => {
    const indice = exibiveis.findIndex((e) => e.id === anexo.id);
    if (indice >= 0) setVisualizando(indice);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-4">
        <Voltar />
        <ErrorAlert
          title="Não foi possível carregar o chamado"
          message={
            (error as { response?: { status?: number } })?.response?.status === 404
              ? equipe
                ? "Este chamado não existe ou não pertence ao cliente atendido."
                : "Este chamado não existe ou não pertence ao seu cliente."
              : (error as Error)?.message ?? "Tente novamente."
          }
          onRetry={() => refetch()}
          variant="error"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Voltar />
      <Cabecalho detalhe={data} />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Descricao texto={data.chamado.descricao} />
          <LinhaDoTempo
            idChamado={idChamado}
            interacoes={data.interacoes}
            anexos={anexos.data ?? []}
            aoVisualizar={abrirVisualizador}
          />
        </div>
        <div className="space-y-6">
          <Anexos
            idChamado={idChamado}
            itens={anexos.data ?? []}
            carregando={anexos.isLoading}
            aoVisualizar={abrirVisualizador}
          />
        </div>
      </div>

      {visualizando !== null && exibiveis.length > 0 && (
        <VisualizadorAnexo
          idChamado={idChamado}
          anexos={exibiveis}
          indiceInicial={visualizando}
          aoFechar={() => setVisualizando(null)}
        />
      )}
    </div>
  );
};

const Voltar = () => (
  <Link
    to="/chamados"
    className="inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900"
  >
    <ArrowLeft className="w-4 h-4" />
    Voltar para a lista
  </Link>
);

/**
 * O cabeçalho da ficha (05/10/2026): além do que identifica o chamado, a faixa com o que se decide
 * olhando para ele — a SLA (situação, tempo e prazo, pela mesma regra do quadro), quem da T4i cuida
 * dele e, para a equipe, de que cliente é. A prioridade aparece ao lado da severidade.
 */
const Cabecalho = ({ detalhe }: { detalhe: DetalheChamado }) => {
  const c = detalhe.chamado;
  const user = useAuthUser();
  const escreve = Boolean(useCapacidades().data?.escritasHabilitadas);
  const temSla = c.segmento !== "Novas";
  const sla = useSlaChamado(c.id, temSla);
  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">
            Chamado #{c.id} · aberto em {formatarData(c.dataCriacao, true)}
          </p>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">{c.assunto}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {c.prioridade != null && (
            <span
              className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-semibold text-slate-700"
              title="Prioridade na fila do cliente"
            >
              Prioridade {c.prioridade}
            </span>
          )}
          <span
            className={[
              "rounded-full border px-3 py-1 text-sm font-semibold",
              classesSeveridade(c.severidade),
            ].join(" ")}
          >
            {rotuloSeveridade(c.severidade)}
          </span>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
        {/* A etiqueta de status abre, ali mesmo, a mudança de status (equipe T4i) ou as ações do
            cliente — o cartão de status saiu da lateral (05/10/2026). */}
        {escreve && user?.equipe ? (
          <EtiquetaStatusEquipe
            idChamado={c.id}
            idStatusAtual={c.idStatusChamado}
            nomeStatusAtual={c.statusChamado ?? "—"}
            className={classeEtiquetaStatus}
          />
        ) : escreve ? (
          <EtiquetaAcoesCliente
            idChamado={c.id}
            nomeStatusAtual={c.statusChamado ?? "—"}
            className={classeEtiquetaStatus}
          />
        ) : (
          <span className={classeEtiquetaStatus}>{c.statusChamado ?? "—"}</span>
        )}
        {temSla && <FaixaSla sla={sla.data} carregando={sla.isLoading} />}
        <span className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-gray-700">
          Responsável T4i:{" "}
          <strong className="font-medium">{c.responsavelT4i ?? "não atribuído"}</strong>
        </span>
        {user?.equipe && user.nomeCliente && (
          <span className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-indigo-800">
            Cliente: <strong className="font-medium">{user.nomeCliente}</strong>
            {user.nomeContrato && <> · {user.nomeContrato}</>}
          </span>
        )}
      </div>

      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-5 text-sm">
        <Campo rotulo="Tipo" valor={c.categoriaChamado} />
        <Campo rotulo="Produto" valor={c.projetoNome} />
        <Campo rotulo="Previsão" valor={formatarData(c.dataPrevisao)} />
        <Campo rotulo="Contato" valor={c.nomeContato} />
        <Campo rotulo="E-mail" valor={c.emailContato} />
        <Campo
          rotulo="Última movimentação"
          valor={formatarData(c.dataModificacao, true)}
        />
      </dl>
    </div>
  );
};

const classeEtiquetaStatus =
  "rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 font-medium text-blue-800";

/** SLA numa pílula: situação, tempo restante e prazo, com a cor da situação (como no quadro). */
const FaixaSla = ({ sla, carregando }: { sla: SlaInfo | undefined; carregando: boolean }) => {
  if (carregando) return <span className="text-xs text-gray-400">SLA…</span>;
  if (!sla) return null;
  const status = sla.statusSla ?? "";
  const naFila = /fila/i.test(status);
  const pausado = /pausad/i.test(status);
  const cor = naFila
    ? "bg-slate-50 text-slate-700 border-slate-200"
    : pausado
      ? "bg-yellow-50 text-yellow-800 border-yellow-200"
      : sla.slaVencido
        ? "bg-red-50 text-red-700 border-red-200"
        : "bg-green-50 text-green-800 border-green-200";
  const restante = !naFila && !sla.slaVencido && sla.tempoRestanteConclusao
    ? formatTempoRestante(sla.tempoRestanteConclusao) || sla.tempoRestanteConclusao
    : null;
  const prazo = !naFila && sla.dataLimiteConclusao ? formatarData(sla.dataLimiteConclusao, true) : null;
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 rounded-lg border px-2.5 py-1 ${cor}`}>
      {sla.slaVencido ? <AlertTriangle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
      <strong className="font-medium">SLA: {status || "—"}</strong>
      {naFila && sla.tempoConclusaoAplicado > 0 && <span>· {sla.tempoConclusaoAplicado}h após ativação</span>}
      {restante && <span>· {pausado ? "faltariam" : "faltam"} {restante}</span>}
      {prazo && !pausado && <span>· prazo {prazo}</span>}
    </span>
  );
};

const Campo = ({ rotulo, valor }: { rotulo: string; valor: string | null }) => (
  <div>
    <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
      {rotulo}
    </dt>
    <dd className="text-gray-800 mt-0.5 break-words">{valor || "—"}</dd>
  </div>
);

const Descricao = ({ texto }: { texto: string | null }) => (
  <section className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-6">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">Descrição</h2>
    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
      {texto?.trim() || "Sem descrição."}
    </p>
  </section>
);

const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

/**
 * A conversa do chamado. Três quartos das interações públicas são registro
 * automático de mudança de status — nos chamados do cliente 9, 3.281 de 4.371 —,
 * e com o mesmo peso visual de um comentário afogavam o que alguém escreveu.
 * Aqui evento é uma linha fina, mensagem é um cartão, e o filtro esconde os
 * eventos de vez para quem quer só a conversa.
 */
export const LinhaDoTempo = ({
  idChamado,
  interacoes,
  anexos,
  aoVisualizar,
}: {
  idChamado: number;
  interacoes: InteracaoChamado[];
  anexos: AnexoChamado[];
  aoVisualizar: (anexo: AnexoChamado) => void;
}) => {
  const [soConversas, setSoConversas] = useState(false);

  const porInteracao = useMemo(() => anexosPorInteracao(anexos), [anexos]);
  const quantosEventos = useMemo(
    () => interacoes.filter(ehEvento).length,
    [interacoes]
  );

  const visiveis = soConversas ? interacoes.filter((i) => !ehEvento(i)) : interacoes;
  const dias = useMemo(() => agruparPorDia(visiveis), [visiveis]);

  return (
    <section className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-6">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-900">
          Linha do tempo
          <span className="ml-2 text-sm font-normal text-gray-500">
            {interacoes.length} interação(ões)
          </span>
        </h2>

        {quantosEventos > 0 && (
          <button
            type="button"
            onClick={() => setSoConversas((v) => !v)}
            className="text-sm font-medium text-blue-700 hover:text-blue-900"
          >
            {soConversas
              ? `Mostrar os ${quantosEventos} eventos`
              : "Só as conversas"}
          </button>
        )}
      </div>

      {visiveis.length === 0 ? (
        <p className="text-gray-500 text-sm">
          {interacoes.length === 0
            ? "Ainda não há interações neste chamado."
            : "Só há registros automáticos neste chamado."}
        </p>
      ) : (
        <ol className="space-y-4">
          {dias.map((dia) => (
            <li key={dia.dia}>
              <div className="mb-2 flex items-center gap-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {dia.rotulo}
                </span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>

              <ol className="space-y-2">
                {dia.interacoes.map((i) =>
                  ehEvento(i) ? (
                    <Evento
                      key={i.id}
                      interacao={i}
                      anexos={porInteracao.get(i.id) ?? []}
                      idChamado={idChamado}
                      aoVisualizar={aoVisualizar}
                    />
                  ) : (
                    <Mensagem
                      key={i.id}
                      interacao={i}
                      anexos={porInteracao.get(i.id) ?? []}
                      idChamado={idChamado}
                      aoVisualizar={aoVisualizar}
                    />
                  )
                )}
              </ol>
            </li>
          ))}
        </ol>
      )}

      <CompositorInteracao idChamado={idChamado} />
    </section>
  );
};

interface ItemLinhaProps {
  interacao: InteracaoChamado;
  anexos: AnexoChamado[];
  idChamado: number;
  aoVisualizar: (anexo: AnexoChamado) => void;
}

/** Registro automático: uma linha, sem cartão, sem avatar. */
const Evento = ({ interacao, anexos, idChamado, aoVisualizar }: ItemLinhaProps) => (
  <li className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1 py-1 text-xs text-gray-500">
    {interacao.interna ? (
      <Lock className="h-3 w-3 shrink-0 text-amber-600" />
    ) : (
      <CircleDot className="h-3 w-3 shrink-0 text-gray-300" />
    )}
    <span className="text-gray-600">{interacao.comentario?.trim()}</span>
    {interacao.interna && <span className="font-medium text-amber-700">interna</span>}
    <span className="text-gray-400" title={interacao.autor ?? undefined}>
      {hora(interacao.dataHora)} · {nomeAutor(interacao)}
    </span>
    {anexos.map((a) => (
      <ChipAnexo
        key={a.id}
        anexo={a}
        idChamado={idChamado}
        aoVisualizar={aoVisualizar}
      />
    ))}
  </li>
);

/**
 * O que alguém escreveu: cartão com autoria, horário e anexos da mensagem.
 * A nota interna da equipe (só a equipe a recebe) é âmbar e tracejada, com o
 * aviso escrito: cor sozinha não basta para dizer "o cliente não vê".
 */
const Mensagem = ({ interacao, anexos, idChamado, aoVisualizar }: ItemLinhaProps) => (
  <li
    className={[
      "flex gap-3 rounded-xl p-4",
      interacao.interna
        ? "border border-dashed border-amber-400 bg-amber-50"
        : interacao.flagConsultor
          ? "border border-blue-100 bg-blue-50/60"
          : "border border-gray-200 bg-white",
    ].join(" ")}
  >
    <span
      className={[
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
        interacao.flagConsultor
          ? "bg-blue-600 text-white"
          : "bg-gray-200 text-gray-700",
      ].join(" ")}
      aria-hidden="true"
    >
      {iniciais(nomeAutor(interacao))}
    </span>

    <div className="min-w-0 flex-1">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium text-gray-800" title={interacao.autor ?? undefined}>
          {nomeAutor(interacao)}
          {interacao.flagConsultor && !interacao.interna && (
            <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-800">
              T4i
            </span>
          )}
          {interacao.interna && (
            <span className="ml-2 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">
              <Lock className="h-3 w-3" />
              Nota interna · o cliente não vê
            </span>
          )}
        </span>
        <time
          className="text-xs text-gray-500"
          title={formatarData(interacao.dataHora, true)}
        >
          {hora(interacao.dataHora)}
        </time>
      </div>

      <p className="whitespace-pre-wrap leading-relaxed text-gray-700">
        {interacao.comentario?.trim() || "—"}
      </p>

      {anexos.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {anexos.map((a) => (
            <ChipAnexo
              key={a.id}
              anexo={a}
              idChamado={idChamado}
              aoVisualizar={aoVisualizar}
            />
          ))}
        </div>
      )}
    </div>
  </li>
);

/**
 * Anexo no corpo da mensagem que o trouxe. A lista lateral continua existindo,
 * mas ali o arquivo aparece solto, sem o texto que explica o que ele é.
 */
const ChipAnexo = ({
  anexo,
  idChamado,
  aoVisualizar,
}: {
  anexo: AnexoChamado;
  idChamado: number;
  aoVisualizar: (anexo: AnexoChamado) => void;
}) => (
  <button
    type="button"
    onClick={() =>
      anexo.exibivelInline ? aoVisualizar(anexo) : baixarAnexo(idChamado, anexo)
    }
    title={`${anexo.denominacao ?? "Anexo"} · ${formatarTamanho(anexo.tamanho)}${
      anexo.interno ? " · interno" : ""
    }`}
    className={[
      "inline-flex max-w-[16rem] items-center gap-1 rounded-full border bg-white px-2 py-0.5 text-xs text-gray-700 transition-colors hover:border-blue-300 hover:text-blue-700",
      anexo.interno ? "border-amber-300" : "border-gray-200",
    ].join(" ")}
  >
    {anexo.interno ? (
      <Lock className="h-3 w-3 shrink-0 text-amber-600" />
    ) : (
      <Paperclip className="h-3 w-3 shrink-0" />
    )}
    <span className="truncate">{anexo.denominacao ?? `Anexo ${anexo.id}`}</span>
  </button>
);

export const Anexos = ({
  idChamado,
  itens,
  carregando,
  aoVisualizar,
}: {
  idChamado: number;
  itens: AnexoChamado[];
  carregando: boolean;
  aoVisualizar: (anexo: AnexoChamado) => void;
}) => {
  const [baixando, setBaixando] = useState<number | null>(null);

  const baixar = async (anexo: AnexoChamado) => {
    setBaixando(anexo.id);
    try {
      await baixarAnexo(idChamado, anexo);
    } finally {
      setBaixando(null);
    }
  };

  return (
    <section className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Paperclip className="w-4 h-4 text-gray-500" />
        Anexos
      </h2>

      {carregando && <p className="text-sm text-gray-500">Carregando…</p>}

      {!carregando && itens.length === 0 && (
        <p className="text-sm text-gray-500">Nenhum anexo neste chamado.</p>
      )}

      <ul className="space-y-2">
        {itens.map((a) => (
          <li
            key={a.id}
            className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2"
          >
            {/* Abrir e baixar são coisas diferentes: o que dá para ver, abre; o
                resto baixa. Antes o clique sempre baixava. */}
            <button
              type="button"
              onClick={() => (a.exibivelInline ? aoVisualizar(a) : baixar(a))}
              disabled={baixando === a.id}
              className="flex min-w-0 flex-1 items-center gap-3 text-left disabled:opacity-60"
            >
              {a.exibivelInline ? (
                <ImageIcon className="w-4 h-4 text-blue-600 shrink-0" />
              ) : (
                <FileText className="w-4 h-4 text-gray-500 shrink-0" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-gray-800">
                  {a.denominacao ?? `Anexo ${a.id}`}
                </span>
                <span className="block text-xs text-gray-500">
                  {formatarTamanho(a.tamanho)} · {formatarData(a.data)}
                </span>
                {a.interno && (
                  <span className="flex items-center gap-1 text-xs font-medium text-amber-700">
                    <Lock className="h-3 w-3" />
                    interno · o cliente não vê
                  </span>
                )}
              </span>
              {a.exibivelInline && (
                <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-blue-700">
                  <Eye className="h-3.5 w-3.5" />
                  ver
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => baixar(a)}
              disabled={baixando === a.id}
              aria-label={`Baixar ${a.denominacao ?? `anexo ${a.id}`}`}
              title="Baixar"
              className="shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-60"
            >
              <Download className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>

    </section>
  );
};

export default ChamadoDetalhePage;
